import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useLocation, Link } from "react-router-dom"; // Importáljuk a useNavigate-t
import { OrderModel, OrderStatus } from "../../../Api/Models/order.model";
import { LaravelValidationError } from "../../../Api/request";
import { orderService } from "../../../Api/Services/OrderService";
import { stockService } from "../../../Api/Services/StockService";
import ImageInput from "../../../Components/ImageInput";
import { useInfoModal } from "../../../Context/InfoModalProvider";
import { useLoading } from "../../../Context/LoadingProvider";
import { LongArrowLeftIcon } from "../../../Components/Icons/LongArrowLeftIcon";
import { AcceptFilledIcon } from "../../../Components/Icons/AcceptFilledIcon";
import { DeclineFilledIcon } from "../../../Components/Icons/DeclineFilledIcon";
import { ModalSuccessIcon } from "../../../Components/Icons/ModalSuccessIcon";
import { HomeIcon } from "../../../Components/Icons/HomeIcon";


interface ShowStockOrderProps {}

const ShowStockOrder: React.FunctionComponent<ShowStockOrderProps> = () => {
  const { startLoading, finnishLoading } = useLoading();

  let { orderId } = useParams();
  const navigate = useNavigate(); // useNavigate használata
  const location = useLocation(); // Hozzáférünk a location state-hez
  const { selectedFilter } = location.state || {}; // selectedFilter elérhető itt

  //const accidentQuantityRef = useRef<HTMLInputElement | null>(null);
  const accidentNoteRef = useRef<HTMLTextAreaElement | null>(null);

  const { showInfo } = useInfoModal();
  const [orderData, setOrderData] = useState<OrderModel>();
  const [report, setReport] = useState<boolean>(false);
  const { t } = useTranslation();
  const inboundFromRef = useRef<HTMLFormElement | null>(null);

  const [images, setImages] = useState<File[]>([]);

  useEffect(() => {
    if (!orderData) {
      if (!orderId) {
        navigate("/stock/orders-list");
        return;
      }
      startLoading("get-order");
      orderService
          .getOrder(parseInt(orderId))
          .then((orderDataModel) => {
            setOrderData(orderDataModel);
          })
          .catch((e) => {
            navigate("/stock/orders-list");
          })
          .finally(() => {
            finnishLoading("get-order");
          });
    }
    //eslint-disable-next-line
  }, []);

  const [receivedQuantity, setReceivedQuantity] = useState<number | "">(0);


  const handleAccidentSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!orderData?.products[0] || !receivedQuantity || receivedQuantity <= 0 || !images.length) {
      showInfo(t("Please fill out all fields."));
      return;
    }

    setReport(false);

    startLoading("report-order-accident");

    stockService
        .report(
            orderData.products[0].id,
            receivedQuantity, // Már a state-ben kezelt receivedQuantity-t küldjük
            accidentNoteRef.current?.value ?? "",
            images
        )
        .then((newOrderData) => {
          setOrderData(newOrderData);

          showInfo(
              <>
                <div className="flex justify-center mb-2"><ModalSuccessIcon /></div>
                <div>{t("Order successfully reported!")}</div>
                <table className="info-table">
                  <tbody>
                  <tr>
                    <th>{t("Expected quantity")}:</th>
                    <td>{orderData.products[0].quantity}</td>
                  </tr>
                  <tr>
                    <th>{t("Received quantity")}:</th>
                    <td>{receivedQuantity}</td>
                  </tr>
                  </tbody>
                </table>
                <div>{t("Please inbound the order now!")}</div>
              </>
          );
        })
        .catch((error: LaravelValidationError) => {
          if (error.errors?.errorMessage) {
            showInfo(error.errors?.errorMessage);
          } else {
            showInfo(t("An unexpected error occurred. Please try again."));
          }
        })
        .finally(() => {
          finnishLoading("report-order-accident");
        });
  };


  const handleOrderDispatched = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (orderData) {
      startLoading("set-order-dispatched");
      stockService
          .dispatch(orderData.id, images)
          .then((newOrderData) => {
            setOrderData(newOrderData);
            showInfo(<><div className="flex justify-center mb-2"><ModalSuccessIcon /></div> {t("Order successfully inbounded!")}</>);
            navigate("/stock/orders/list", {
              state: { selectedFilter }, // selectedFilter visszaadása navigáláskor
            });
          })
          .catch((error: LaravelValidationError) => {
            if (error.errors?.errorMessage) {
              showInfo(error.errors?.errorMessage);
            }
          })
          .finally(() => {
            finnishLoading("set-order-dispatched");
          });
    }
  };

  if (!orderData) {
    return (
        <div className="view-container background">
          <div className="view-content">

            <div className="navigationbar navigationbar mb-6">
              <div className="navigationbar__left">
                <Link to="/menu" className="navigationbar__button">
                  <HomeIcon />
                </Link>
              </div>
              <div className="navigationbar__center">
                <div className="navigationbar__title">{t("Product inbound")}</div>
              </div>
              <div className="navigationbar__right">
              </div>
            </div>
            
          </div>
        </div>
    );
  }

  

  return (
      <div className="view-container background">
        <div className="view-content" style={{ paddingTop: "24px" }}>

          <div className="navigationbar navigationbar mb-6">
            <div className="navigationbar__left">
              <div className="navigationbar__button" onClick={() =>
                  navigate("/stock/orders/list", {
                    state: {selectedFilter},  // Visszaadod a selectedFilter-t a visszalépéskor
                  })
                }>
                <LongArrowLeftIcon />
              </div>
            </div>
            <div className="navigationbar__center">
              <div className="navigationbar__title">{t("Product inbound")}</div>
            </div>
            <div className="navigationbar__right">
            </div>
          </div>

          {report && (
              <div
                  className="bottom-modal"
                  tabIndex={-1}
                  role="dialog"
              >
                <div className="bottom-modal__scroll-content">
                  <div className="bottom-modal__body" style={{ marginTop: "50px" }}>

                    <div className="stock-item__image">
                      <img
                            src={orderData.products[0].image ?? ""}
                            alt="Product"
                            style={{ height: "60px" }}
                        />
                    </div>  

                    <div className="stock-item__title heading text-center mb-6 mt-8">
                      {orderData.products[0]?.name ?? ""}
                    </div>

                    <table className="info-table w-full" >
                      <tbody>
                        <tr>
                          <th>{t("Order ID")}:</th>
                          <td>{orderData.number}</td>
                        </tr>   

                        <tr>
                          <th>{t("Ordered quantity:")}</th>
                          <td>{orderData.products[0].quantity ?? ""}</td>
                        </tr>   
                      </tbody>
                    </table>

                    <div className="separator-line mb-4" style={{paddingTop: '10px'}}></div>


                    <form onSubmit={handleAccidentSubmit}>


                      <label className="text-secondary">{t("Received quantity")}</label>
                      <input
                          type="number"
                          step={1}
                          min={0}
                          max={9999999}
                          value={receivedQuantity}
                          onChange={(e) => setReceivedQuantity(Number(e.target.value))}
                          onFocus={(e) => e.target.select()}
                          placeholder={t("Received quantity") ?? ""}
                          className="input-field mb-2"
                          required
                      />
                      <label className="text-secondary">{t("Note")}</label>
                      <textarea
                          ref={accidentNoteRef}
                          placeholder={t("Note") ?? ""}
                          className="input-field mb-6"
                          required
                      ></textarea>


                      <div className="flex gap-2">
                        <div className="flex-1">
                          <div className="button button--light" onClick={() => setReport(false)}>
                            {t("Back")}
                          </div>
                        </div>
                        <div className="flex-1">
                          <button type="submit" className="button">
                            {t("Save")}
                          </button>
                        </div>
                      </div>


                    </form>
                  </div>
                </div>
              </div>
          )}


          <div className="card mb-4">
            <div className="flex mb-2">
              <div className="order-list-card__left flex">
                <div className="relative">
                  <div className="order-list-card__image">
                    <img
                    src={orderData.products[0].image ?? ""}
                    alt="Product"
                    style={{ height: "60px", margin: "auto" }}
                />
                  </div>

                  {orderData.products[0].variationName && (
                      <div className="order-list-card__variation-name">
                        {orderData.products[0].variationName}
                      </div>
                  )}


                </div>
                <div>
                  <div className="stock-list-card__title">
                    {orderData.products[0].name ?? ""}
                  </div>
                </div>
              </div>
            </div>

            <table className="info-table">
              <tbody>
              <tr>
                <th>{t("Distribution center")}:</th>
                <td>{orderData.distributionCenter}</td>
              </tr>
              <tr>
                <th>{t("Status")}:</th>
                <td>{OrderStatus[orderData.status].toString()}</td>
              </tr>
              <tr>
                <th>{t("Order ID")}:</th>
                <td>{orderData.number}</td>
              </tr>
              <tr>
                <th>{t("Order date")}:</th>
                <td>{orderData.orderDate}</td>
              </tr>
              <tr>
                <th>{t("Product")}:</th>
                <td>
                  {orderData.products
                      .map((product) => product.name)
                      .join(", ")}
                </td>
              </tr>
              <tr>
                <th>{t("MSD code")}:</th>
                <td>
                  {orderData.products[0]?.barcode ?? "-"}
                </td>
              </tr>
              <tr>
                <th>{t("Quantity")}:</th>
                <td>
                  {orderData.products[0]?.quantity ? (
                      <>
                        {orderData.products[0].quantity}
                        &nbsp;{t("Pcs.")}
                      </>
                  ) : null}
                  {orderData.products[0].receivedQuantity ? (
                      <>
                        &nbsp;&nbsp;({t("Received")}:&nbsp;
                        {orderData.products[0].receivedQuantity}
                        &nbsp;{t("Pcs.")})
                      </>
                  ) : null}
                </td>
              </tr>
              <tr>
                <th>{t("Note")}:</th>
                <td>{orderData.note}</td>
              </tr>
              <tr>
                <th>{t("Inbound date")}:</th>
                <td>{orderData.inboundDate}</td>
              </tr>
              </tbody>
            </table>
          </div>

          <div className="">
            {orderData.status !== OrderStatus.INBOUNDED &&
                orderData.status !== OrderStatus.DECLINED &&
                orderData.status !== OrderStatus.PENDING &&
                orderData.status !== OrderStatus.APPROVED_LVL1 && (
              <form
                  onSubmit={handleOrderDispatched}
                  className=""
                  ref={inboundFromRef}
              >
                <div className="card mb-4">
                  <ImageInput images={images} setImages={setImages} />
                </div>

                <div className={"flex gap-4 " + (images.length === 0 ? "disabled-layer" : "")}>
                  <button
                      type="submit"
                      className="button-2 button-2--approved w-full"
                  >
                    <div className="button-2__icon">
                      {/*<i className="icon-approve"></i>*/}
                      <AcceptFilledIcon/>
                    </div>
                    <span className="button-2__text">{t("Inbound")}</span>
                  </button>

                  <button
                      type="button"
                      disabled={orderData.hasAccident}
                      className={
                          "button-2 w-full " +
                          (orderData.hasAccident
                              ? "button-2--disabled"
                              : "button-2--declined")
                      }
                      onClick={() => {
                        if (
                            inboundFromRef.current &&
                            inboundFromRef.current.reportValidity() &&
                            !orderData.hasAccident
                        ) {
                          setReport(true);
                        }
                      }}
                  >
                    <div className="button-2__icon">
                      {/*<i className="icon-refuse"></i>*/}
                      <DeclineFilledIcon/>
                    </div>
                    <span className="button-2__text">{t("Report")}</span>
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
  );
};

export default ShowStockOrder;
